'use client';

import { useMoney } from '@/backend/firebase/hooks/finances/useMoney';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { MoneyInput } from '@/components/money-input';
import { cn } from '@/lib/utils';
import { type CurrenciesType } from '@/shared/schema';
import {
  ArrowDownUp,
  RotateCcw,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface DolarCalculatorDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  tasaOficial: number;
  lastUpdate?: string;
}

export function DolarCalculatorDialog({
  isOpen,
  onOpenChange,
}: DolarCalculatorDialogProps) {
  const { currentTasa, eurToBs } = useMoney();
  const [baseCurrency, setBaseCurrency] =
    useState<Extract<CurrenciesType, 'USD' | 'EUR'>>('USD');
  const [foreignValue, setForeignValue] = useState<number>(1);
  const [bsValue, setBsValue] = useState<number>(0);

  const activeRate = baseCurrency === 'USD' ? currentTasa : eurToBs;

  useEffect(() => {
    if (isOpen) {
      setForeignValue(1);
      setBsValue(activeRate);
    }
  }, [isOpen, baseCurrency, activeRate]);

  const handleForeignChange = (val: number) => {
    setForeignValue(val);
    setBsValue(Number((val * activeRate).toFixed(2)));
  };

  const handleBsChange = (val: number) => {
    setBsValue(val);
    setForeignValue(Number((val / activeRate).toFixed(2)));
  };

  const handleReset = () => {
    setForeignValue(1);
    setBsValue(activeRate);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-full w-full sm:max-w-md mx-auto overflow-hidden border-none p-0 shadow-2xl bg-white flex flex-col outline-none top-0 translate-y-0"
      >
        {/* HEADER QUIRÚRGICO NEGRO */}
        <header className="shrink-0 bg-zinc-950 px-6 h-16 flex items-center justify-between z-50">
          <button 
            onClick={() => onOpenChange(false)}
            className="h-10 px-3 rounded-xl text-zinc-400 font-black text-[11px] uppercase tracking-widest active:bg-white/10"
          >
            Cerrar
          </button>

          <div className="flex gap-1 bg-white/5 p-1 rounded-xl">
            {(['USD', 'EUR'] as const).map((curr) => (
              <button
                key={curr}
                onClick={() => setBaseCurrency(curr)}
                className={cn(
                  'px-4 py-1.5 rounded-lg text-[10px] font-black transition-all',
                  baseCurrency === curr
                    ? 'bg-white text-zinc-900 shadow-md'
                    : 'text-white/30 hover:text-white',
                )}
              >
                {curr}
              </button>
            ))}
          </div>

          <button
            onClick={() => onOpenChange(false)}
            className="h-10 px-5 rounded-full font-black text-[11px] uppercase tracking-widest bg-white text-zinc-950"
          >
            Listo
          </button>
        </header>

        {/* CONTENIDO ULTRA COMPACTO */}
        <div className="flex-1 overflow-y-auto bg-white p-6 pt-4 space-y-6">
          
          <div className="text-center">
            <p className="text-[10px] font-black uppercase text-zinc-300 tracking-[0.3em]">Tasa BCV actual</p>
            <p className="text-2xl font-black italic tracking-tighter text-zinc-900 leading-none mt-1">Bs {activeRate.toFixed(2)}</p>
          </div>

          <div className="relative flex flex-col gap-3">
            <div className="space-y-1.5">
              <label className="text-[9px] font-black text-zinc-400 uppercase ml-2 tracking-widest">Monto {baseCurrency}</label>
              <MoneyInput
                value={foreignValue}
                onChange={handleForeignChange}
                currency={baseCurrency}
                allowCurrencyChange={false}
              />
            </div>

            <div className="flex justify-center -my-1 relative z-10">
               <div className="bg-white border-2 border-zinc-100 p-1.5 rounded-xl text-zinc-300">
                  <ArrowDownUp className="h-4 w-4" />
               </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[9px] font-black text-zinc-400 uppercase ml-2 tracking-widest">Monto Bolívares</label>
              <MoneyInput
                value={bsValue}
                onChange={handleBsChange}
                currency="BS"
                allowCurrencyChange={false}
              />
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <button
              onClick={handleReset}
              className="text-zinc-400 hover:text-zinc-900 flex items-center gap-2 p-2 text-[9px] font-black tracking-widest uppercase active:scale-95"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Restaurar
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
